import { useState, useMemo } from 'react';
import { useFeed } from '../../hooks/useFeed';
import { FeedItem } from './FeedItem';
import { PasteMaterial } from '../PasteMaterial/PasteMaterial';
import { Link, useParams } from 'react-router-dom';
import { relativeTime } from '@news-assist/shared';
import type { FeedItemWithMeta } from '@news-assist/shared';

type RecencyFilter = 'all' | 'breaking' | 'developing' | 'context';
type TypeFilter = 'all' | 'NEWS' | 'STATISTIC' | 'QUOTE' | 'OFFICIAL' | 'ALLEGATION';
type UrgencyFilter = 'all' | 'high' | 'normal';

export function FeedPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const orgPrefix = orgSlug ? `/${orgSlug}` : '';

  const [recencyFilter, setRecencyFilter] = useState<RecencyFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [urgencyFilter, setUrgencyFilter] = useState<UrgencyFilter>('all');
  const [sortByUrgency, setSortByUrgency] = useState(false);
  const [groupByStory, setGroupByStory] = useState(true);


  // Map UI type filter to API parameters
  const getApiParams = () => {
    const params: Record<string, string | boolean> = {};

    // Filter by claim type (matches database claim_type column)
    if (typeFilter !== 'all') {
      params.claimType = typeFilter;
    }

    // Urgency filter: high = 1,2,3 (flash/breaking/urgent)
    if (urgencyFilter === 'high') params.urgency = '1,2,3';

    if (sortByUrgency) params.sortByUrgency = 'true';

    return params;
  };

  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useFeed({
      ...getApiParams(),
      recency: recencyFilter,
    });

  const items = data?.pages.flatMap((page) => page.items) || [];

  // Extract date from claim content like "text [2026-01-27]"
  const extractClaimDate = (headline: string): string | null => {
    const match = headline.match(/\[(\d{4}-\d{2}-\d{2})\]/);
    return match?.[1] ?? null;
  };

  // Group items by story + time window if groupByStory is enabled
  // Claims from the same story are only grouped if they arrived within TIME_WINDOW_HOURS of each other
  const groupedItems = useMemo(() => {
    if (!groupByStory) return null;

    const TIME_WINDOW_HOURS = 4; // Group claims within this many hours
    const TIME_WINDOW_MS = TIME_WINDOW_HOURS * 60 * 60 * 1000;

    // Track the latest timestamp for each story's current group
    const storyLastTimestamp = new Map<string, number>();
    const storyGroupCounter = new Map<string, number>();

    const groups: Array<{
      groupKey: string;
      storyId: string;
      storyTitle: string;
      storySlug: string;
      items: FeedItemWithMeta[];
    }> = [];

    const groupMap = new Map<string, typeof groups[0]>();

    // Items are already sorted by time (newest first) from the API
    for (const item of items) {
      const storyId = item.storyId || 'ungrouped';
      const itemTime = item.publishedAt ? new Date(item.publishedAt).getTime() : Date.now();

      // Check if we should start a new group for this story
      const lastTime = storyLastTimestamp.get(storyId);
      let groupKey: string;

      if (lastTime === undefined || Math.abs(itemTime - lastTime) > TIME_WINDOW_MS) {
        // Start a new group for this story
        const counter = (storyGroupCounter.get(storyId) || 0) + 1;
        storyGroupCounter.set(storyId, counter);
        groupKey = `${storyId}-${counter}`;

        const newGroup = {
          groupKey,
          storyId,
          storyTitle: item.storyTitle || 'Ungrouped',
          storySlug: item.storySlug || '',
          items: [],
        };
        groups.push(newGroup);
        groupMap.set(groupKey, newGroup);
      } else {
        // Add to existing group
        groupKey = `${storyId}-${storyGroupCounter.get(storyId)}`;
      }

      storyLastTimestamp.set(storyId, itemTime);
      groupMap.get(groupKey)!.items.push(item);
    }

    // Sort items within each group by claim date (newest first)
    for (const group of groups) {
      group.items.sort((a, b) => {
        const dateA = extractClaimDate(a.headline) || '0000-00-00';
        const dateB = extractClaimDate(b.headline) || '0000-00-00';
        return dateB.localeCompare(dateA); // Descending (newest first)
      });
    }

    // Groups are already in feed order (most recent activity first)
    return groups;
  }, [items, groupByStory]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-500">Loading feed...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error.message}
      </div>
    );
  }

  // Check if any filters are active
  const hasActiveFilters = recencyFilter !== 'all' || typeFilter !== 'all' || urgencyFilter !== 'all';

  if (items.length === 0) {
    return (
      <div>
        <div className="mb-4">
          <PasteMaterial />
        </div>
        {hasActiveFilters ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No items match your current filters.</p>
            <button
              onClick={() => {
                setRecencyFilter('all');
                setTypeFilter('all');
                setUrgencyFilter('all');
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <p>No news yet. Use the browser extension to clip articles.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <PasteMaterial />
      </div>

      {/* Filter controls */}
      <div className="mb-4 flex flex-wrap items-center gap-4 p-3 bg-gray-50 rounded-lg">
        {/* Recency filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show:</span>
          <div className="flex gap-1">
            {(['all', 'breaking', 'developing', 'context'] as RecencyFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setRecencyFilter(recencyFilter === f ? 'all' : f)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  recencyFilter === f
                    ? f === 'breaking'
                      ? 'bg-red-600 text-white'
                      : f === 'developing'
                      ? 'bg-yellow-500 text-white'
                      : f === 'context'
                      ? 'bg-gray-500 text-white'
                      : 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Type filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Type:</span>
          <div className="flex gap-1">
            {(['all', 'NEWS', 'QUOTE', 'STATISTIC', 'OFFICIAL'] as TypeFilter[]).map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  typeFilter === t
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t === 'all' ? 'All' : t.charAt(0) + t.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Urgency filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Priority:</span>
          <div className="flex gap-1">
            {(['all', 'high'] as UrgencyFilter[]).map((u) => (
              <button
                key={u}
                onClick={() => setUrgencyFilter(u)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  urgencyFilter === u
                    ? u === 'high'
                      ? 'bg-red-600 text-white'
                      : 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                }`}
              >
                {u === 'all' ? 'All' : 'High Priority'}
              </button>
            ))}
          </div>
        </div>

        {/* Sort by urgency toggle */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sortByUrgency}
              onChange={(e) => setSortByUrgency(e.target.checked)}
              className="rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm text-gray-600">Breaking first</span>
          </label>
        </div>

        {/* Group by story toggle */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={groupByStory}
              onChange={(e) => setGroupByStory(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">Group by story</span>
          </label>
        </div>

        {/* Item count */}
        <div className="text-sm text-gray-500 ml-auto">
          {items.length} items
        </div>
      </div>

      {/* Feed content */}
      {groupByStory && groupedItems ? (
        <div className="space-y-4">
          {groupedItems.map((group) => {
            // Dedupe sources for this story group
            const uniqueSources = [...new Set(group.items.map(i => i.sourceName).filter(Boolean))];
            const latestTime = group.items[0]?.publishedAt;

            return (
              <div key={group.groupKey} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Story header */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-start justify-between gap-4">
                    <Link
                      to={`${orgPrefix}/story/${group.storyId}`}
                      className="text-base font-semibold text-gray-900 hover:text-blue-600"
                    >
                      {group.storyTitle}
                    </Link>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {latestTime && relativeTime(latestTime)}
                    </span>
                  </div>
                  {uniqueSources.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {uniqueSources.slice(0, 4).map(source => (
                        <span key={source} className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                          {source}
                        </span>
                      ))}
                      {uniqueSources.length > 4 && (
                        <span className="text-xs text-gray-400">+{uniqueSources.length - 4} more</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Compact claim list */}
                <ul className="px-4 py-3 space-y-2">
                  {group.items.slice(0, 8).map((item) => {
                    const isQuote = item.itemType === 'quote';
                    const urgency = item.urgency ?? 4;

                    // Dot color based on urgency (matching FeedItem badges)
                    const getDotColor = () => {
                      if (urgency === 1) return 'bg-red-600';      // Flash
                      if (urgency === 2) return 'bg-red-500';      // Breaking
                      if (urgency === 3) return 'bg-orange-500';   // Urgent
                      if (isQuote) return 'bg-purple-500';         // Quote
                      return 'bg-gray-300';                        // Normal
                    };

                    return (
                      <li key={item.id} className="flex items-start gap-2 text-sm">
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${getDotColor()}`} />
                        <span className={`flex-1 ${isQuote ? 'italic text-gray-700' : 'text-gray-800'}`}>
                          {urgency <= 2 && <span className="text-red-600 font-semibold text-xs mr-1">[{item.urgencyLabel?.toUpperCase() || (urgency === 1 ? 'FLASH' : 'BREAKING')}]</span>}
                          {isQuote && <span className="text-gray-400">"</span>}
                          {item.headline}
                          {isQuote && <span className="text-gray-400">"</span>}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {group.items.length > 8 && (
                  <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-center">
                    <Link
                      to={`${orgPrefix}/story/${group.storyId}`}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View all {group.items.length} claims
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
          {items.map((item) => (
            <FeedItem key={item.id} item={item} />
          ))}
        </div>
      )}

      {hasNextPage && (
        <div className="mt-4 text-center">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
